import React, { useEffect, useState } from "react";
import styles from "./languageSelectorModalMobile.module.scss";
import Button from "@Components/Button";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useLanguagePath from "hooks/useLanguagePath";
import useLanguageSelector from "hooks/useLanguageSelector";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { getPickerLanguages } from "@Utils/languageCore";
import currencyData, { ALLOWED_CURRENCY_CODES } from "constants/currencyConstants";
import getCookie from "helpers/getCookie";
import useIsApp from "hooks/useIsApp";
import config from "@Config/index";
import { renderChinaLanguageLabel } from "./chinaLanguageLabels";
import { persistUserCurrency, reloadAfterModalClose } from "./persistUserCurrency";

interface LanguageSelectorModalMobileProps {
  onClose: () => void;
  isCurrencyFeatureEnabled: boolean;
  initialTab?: "language" | "currency";
}

const LanguageSelectorModalMobile: React.FC<LanguageSelectorModalMobileProps> = ({
  onClose,
  isCurrencyFeatureEnabled,
  initialTab = "language",
}) => {
  const { t } = useTranslationHook();
  const isApp = useIsApp();

  // Never empty: URL prefix, else redux / "zh" on .cn, else "en".
  const { userLanguage: preferredLanguage } = useLanguagePath();
  const isMiniProgram = useIsMiniProgram();
  const showNewLocales = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_NEW_LOCALES);

  const languages = getPickerLanguages({
    currentLanguage: preferredLanguage,
    showNewLocales,
    isMiniProgram,
    isApp,
  });
  const defaultLanguage = languages.some((lang) => lang.code === preferredLanguage)
    ? preferredLanguage
    : languages[0].code;

  const handleEventEmitter = ({ action, ...rest }: any) => {
    gtagEventEmitter({ action, category: "LANGUAGE_MODAL", ...rest });
  };

  const { handleLanguageChange } = useLanguageSelector({ handleEventEmitter });

  const [activeTab, setActiveTab] = useState<"language" | "currency">(
    initialTab === "currency" && isCurrencyFeatureEnabled ? "currency" : "language",
  );
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [selectedCurrency, setSelectedCurrency] = useState("default");
  const [currencies, setCurrencies] = useState<typeof currencyData>([]);

  /** Sync language with redux */
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  /** Setup currency list + cookie ordering */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled) return;
    const userCurrencyCookie = getCookie("user_currency");
    const filteredCurrencies = currencyData.filter((cur) =>
      ALLOWED_CURRENCY_CODES.includes(cur.standard_name),
    );
    const orderedCurrencies = [...filteredCurrencies];
    if (userCurrencyCookie) {
      setSelectedCurrency(userCurrencyCookie);
      orderedCurrencies.sort((a, b) => {
        if (a.standard_name === userCurrencyCookie) return -1;
        if (b.standard_name === userCurrencyCookie) return 1;
        return 0;
      });
    }
    setCurrencies(orderedCurrencies);
  }, [isCurrencyFeatureEnabled]);

  /** Safety: reset tab if currency feature is disabled */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled && activeTab === "currency") {
      setActiveTab("language");
    }
  }, [isCurrencyFeatureEnabled, activeTab]);

  const saveCurrency = (currency: string) => {
    handleEventEmitter({ action: "APPLY_CURRENCY", currency });
    persistUserCurrency(currency, { notifyNativeApp: isApp });
  };

  const onApply = () => {
    // Close modal first to ensure clean state before navigation
    onClose();

    // Whitelabel applies every preference the user changed, not only the open tab: picking a
    // language and then a currency must apply both (POD3-2239). Other builds keep applying the
    // open tab only. The currency cookie is written first so the language navigation (a full
    // page load) already renders in the new currency.
    const applyAllChanges = config.IS_WHITELABEL_APP;
    const currencyChanged =
      isCurrencyFeatureEnabled && selectedCurrency !== (getCookie("user_currency") || "default");
    const applyLanguage =
      activeTab === "language" || (applyAllChanges && selectedLanguage !== defaultLanguage);
    const applyCurrency = activeTab === "currency" || (applyAllChanges && currencyChanged);

    if (applyCurrency) saveCurrency(selectedCurrency);

    if (applyLanguage) {
      handleEventEmitter({ action: "APPLY_LANGUAGE", language: selectedLanguage });
      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        handleLanguageChange(selectedLanguage);
      }, 100);
    } else {
      reloadAfterModalClose();
    }
  };

  // Reset applies straight away: the default currency takes effect without a separate Apply click.
  // The language selection only reverts to the current language, so there is nothing to navigate to.
  const onReset = () => {
    setSelectedLanguage(defaultLanguage);
    onClose();
    if (isCurrencyFeatureEnabled && getCookie("user_currency")) {
      saveCurrency("default");
      reloadAfterModalClose();
    }
  };

  const handleActiveTabChange = (tab: "language" | "currency") => {
    setActiveTab(tab);
    handleEventEmitter({ action: `clicked_${tab}_tab` });
  };

  const isChinaUI = config.IS_CHINA_APP;

  return (
    <div className={formatClassNames(styles.container, isChinaUI && styles.china)}>
      <div className={styles.modal_header}>
        <h4>{t("Select Preferences")}</h4>
      </div>

      <div className={styles.body}>
        <div className={styles.sidebar}>
          <div
            className={formatClassNames(
              styles.sidebarItem,
              activeTab === "language" && styles.active,
            )}
            onClick={() => handleActiveTabChange("language")}
            role="none"
          >
            {t("Language")}
            <span className={styles.checkIcon}>{activeTab === "language" ? "✓" : ""}</span>
          </div>

          {isCurrencyFeatureEnabled && (
            <div
              className={formatClassNames(
                styles.sidebarItem,
                activeTab === "currency" && styles.active,
              )}
              onClick={() => handleActiveTabChange("currency")}
              role="none"
            >
              {t("Currency")}
              <span className={styles.checkIcon}>{activeTab === "currency" ? "✓" : ""}</span>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <ul className={styles.list}>
            {activeTab === "language" &&
              languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.option,
                      selectedLanguage === lang.code && styles.active,
                    )}
                    onClick={() => setSelectedLanguage(lang.code)}
                  >
                    {(isChinaUI && renderChinaLanguageLabel(lang.code, styles)) || (
                      <span className={styles.left}>
                        {lang.flag && React.createElement(lang.flag)}
                        <span className={styles.optionLabel}>{lang.label}</span>
                      </span>
                    )}
                    <span
                      className={formatClassNames(
                        styles.radio,
                        selectedLanguage === lang.code && styles.checked,
                      )}
                    />
                  </button>
                </li>
              ))}

            {isCurrencyFeatureEnabled &&
              activeTab === "currency" &&
              currencies.map((cur, index) => (
                <React.Fragment key={cur.standard_name}>
                  {index === 0 && (
                    <li>
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.option,
                          selectedCurrency === "default" && styles.optionActive,
                        )}
                        onClick={() => setSelectedCurrency("default")}
                      >
                        <span className={styles.left}>
                          <span className={styles.optionLabel}>Default</span>
                        </span>
                        <span
                          className={formatClassNames(
                            styles.radio,
                            selectedCurrency === "default" && styles.checked,
                          )}
                        />
                      </button>
                    </li>
                  )}

                  <li>
                    <button
                      type="button"
                      className={formatClassNames(
                        styles.option,
                        selectedCurrency === cur.standard_name && styles.active,
                      )}
                      onClick={() => setSelectedCurrency(cur.standard_name)}
                    >
                      <span className={styles.left}>
                        <span className={styles.optionLabel}>
                          {`${cur.standard_name} (${cur.symbol}) · ${
                            cur?.full_name?.split("(")[0]
                          }`}
                        </span>
                      </span>
                      <span
                        className={formatClassNames(
                          styles.radio,
                          selectedCurrency === cur.standard_name && styles.checked,
                        )}
                      />
                    </button>
                  </li>
                </React.Fragment>
              ))}
          </ul>
        </div>
      </div>

      <div className={styles.modal_footer}>
        <Button
          className={isChinaUI ? styles.chinaReset : styles.btn_width}
          variant="outline"
          onClick={onReset}
          greyDisabled={
            selectedLanguage === defaultLanguage &&
            (!isCurrencyFeatureEnabled || selectedCurrency === "default")
          }
        >
          {t("Reset")}
        </Button>
        <Button
          className={isChinaUI ? styles.chinaApply : styles.btn_apply}
          variant="primary"
          onClick={onApply}
        >
          {t("Apply")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(LanguageSelectorModalMobile, {
  componentName: "LanguageSelectorModalMobile",
  showForChina: true,
});
